import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import API_URL from '../config/api';
import { getAdminAuthHeader } from '../utils/adminAuth';

function GameManagement() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchGames();
  }, []);

  const fetchGames = async () => {
    try {
      setLoading(true);
      setError(null);
      const authHeader = getAdminAuthHeader();
      
      const response = await fetch(`${API_URL}/admin/games`, {
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch games');
      }

      const data = await response.json();
      // Sort by order
      const sortedGames = [...data].sort((a, b) => a.order - b.order);
      setGames(sortedGames);
      setHasChanges(false);
    } catch (err) {
      console.error('Error fetching games:', err);
      setError('Failed to load games');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setGames((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newGames = arrayMove(items, oldIndex, newIndex);
        // Update order values
        const updatedGames = newGames.map((game, index) => ({
          ...game,
          order: index + 1,
        }));
        setHasChanges(true);
        return updatedGames;
      });
    }
  };

  const handleToggleFeatured = (gameId) => {
    setGames((items) => {
      // If marking as featured, unfeature the previously featured game
      const currentlyFeatured = items.find((game) => game.featured);
      const isMarkingAsFeatured = !items.find((game) => game.id === gameId)?.featured;

      const updated = items.map((game) => {
        if (game.id === gameId) {
          return { ...game, featured: !game.featured };
        }
        // If marking a new game as featured, unfeature the old one
        if (isMarkingAsFeatured && currentlyFeatured && game.id === currentlyFeatured.id) {
          return { ...game, featured: false };
        }
        return game;
      });
      setHasChanges(true);
      return updated;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const authHeader = getAdminAuthHeader();

      // Prepare updates: order and featured status
      const updates = games.map((game) => ({
        id: game.id,
        order: game.order,
        featured: game.featured,
      }));

      const response = await fetch(`${API_URL}/admin/games/update`, {
        method: 'PUT',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ games: updates }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save changes');
      }

      setSuccess('Games updated successfully!');
      setHasChanges(false);
      
      // Refresh games to get latest data
      setTimeout(() => {
        fetchGames();
      }, 1000);
    } catch (err) {
      console.error('Error saving games:', err);
      setError(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-white/60">Loading games...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white mb-1">Game Management</h2>
          <div className="text-white/40 text-xs uppercase tracking-wider">Reorder and manage featured games</div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="px-6 py-3 bg-accent-blue/20 border border-accent-blue/30 rounded-xl text-accent-blue font-medium hover:bg-accent-blue/30 hover:border-accent-blue/40 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-green-400">
          {success}
        </div>
      )}

      {hasChanges && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 text-yellow-400">
          You have unsaved changes. Click "Save Changes" to apply them.
        </div>
      )}

      {/* Featured Game Section */}
      <div className="bg-dark-card border border-dark-border rounded-lg p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-white mb-1">Featured Game</h3>
          <div className="text-white/40 text-xs uppercase tracking-wider">Only one game can be featured at a time</div>
        </div>
        {games.find((game) => game.featured) ? (
          <FeaturedGameItem
            game={games.find((game) => game.featured)}
            onToggleFeatured={handleToggleFeatured}
          />
        ) : (
          <div className="bg-dark-bg/30 border border-dashed border-dark-border rounded-lg p-8 text-center">
            <div className="text-white/40 text-sm">No featured game selected</div>
            <div className="text-white/20 text-xs mt-2">Click the star icon on any game below to feature it</div>
          </div>
        )}
      </div>

      {/* Game Ordering Section */}
      <div className="bg-dark-card border border-dark-border rounded-lg p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-white mb-1">Game Order</h3>
          <div className="text-white/60 text-sm mb-2">
            Drag and drop games to reorder them. The featured game is excluded from this list.
          </div>
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={games.filter((game) => !game.featured).map((game) => game.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {games
                .filter((game) => !game.featured)
                .sort((a, b) => a.order - b.order)
                .map((game) => (
                  <SortableGameItem
                    key={game.id}
                    game={game}
                    onToggleFeatured={handleToggleFeatured}
                  />
                ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
}

function SortableGameItem({ game, onToggleFeatured }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: game.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-dark-bg/30 border border-dark-border rounded-lg p-4 ${
        isDragging ? 'shadow-lg' : ''
      } hover:border-white/20 transition-all`}
    >
      <div className="flex items-center gap-4">
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-white/40 hover:text-white/60 transition-colors"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <circle cx="5" cy="5" r="1.5" />
            <circle cx="5" cy="10" r="1.5" />
            <circle cx="5" cy="15" r="1.5" />
            <circle cx="10" cy="5" r="1.5" />
            <circle cx="10" cy="10" r="1.5" />
            <circle cx="10" cy="15" r="1.5" />
            <circle cx="15" cy="5" r="1.5" />
            <circle cx="15" cy="10" r="1.5" />
            <circle cx="15" cy="15" r="1.5" />
          </svg>
        </div>

        {/* Order Number */}
        <div className="w-10 text-center">
          <div className="text-white/40 text-xs uppercase tracking-wider mb-1">Order</div>
          <div className="text-white font-mono font-bold text-lg">{game.order}</div>
        </div>

        {/* Game Image */}
        {game.featuredImage && (
          <img
            src={game.featuredImage}
            alt={game.title}
            className="w-16 h-16 rounded-lg object-cover"
          />
        )}

        {/* Game Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-white font-semibold text-lg truncate">{game.title}</h3>
            {game.featured && (
              <span className="px-2 py-0.5 bg-accent-blue/20 border border-accent-blue/30 rounded text-accent-blue text-xs font-medium">
                FEATURED
              </span>
            )}
          </div>
          <div className="text-white/50 text-sm">{game.developer}</div>
          <div className="text-white/30 text-xs mt-1">
            ID: {game.id} | Shards: {game.shards}
          </div>
        </div>

        {/* Featured Toggle */}
        <button
          onClick={() => onToggleFeatured(game.id)}
          className={`p-3 rounded-lg border transition-all ${
            game.featured
              ? 'bg-yellow-500/20 border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/30'
              : 'bg-dark-bg/50 border-dark-border text-white/40 hover:border-white/20 hover:text-white/60'
          }`}
          title={game.featured ? 'Remove from featured' : 'Mark as featured'}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill={game.featured ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function FeaturedGameItem({ game, onToggleFeatured }) {
  return (
    <div className="bg-dark-bg/30 border-2 border-yellow-500/30 rounded-lg p-4">
      <div className="flex items-center gap-4">
        {/* Game Image */}
        {game.featuredImage && (
          <img
            src={game.featuredImage}
            alt={game.title}
            className="w-20 h-20 rounded-lg object-cover"
          />
        )}

        {/* Game Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-white font-semibold text-lg truncate">{game.title}</h3>
            <span className="px-2 py-0.5 bg-yellow-500/20 border border-yellow-500/30 rounded text-yellow-400 text-xs font-medium">
              FEATURED
            </span>
          </div>
          <div className="text-white/50 text-sm">{game.developer}</div>
          <div className="text-white/30 text-xs mt-1">
            ID: {game.id} | Shards: {game.shards}
          </div>
        </div>

        {/* Remove Featured Button */}
        <button
          onClick={() => onToggleFeatured(game.id)}
          className="px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 font-medium hover:bg-red-500/30 hover:border-red-500/40 transition-all duration-200"
          title="Remove from featured"
        >
          Remove Featured
        </button>
      </div>
    </div>
  );
}

export default GameManagement;

